/**
 * Utility module that generates all LN2 message options from the auto-generated
 * FlatBuffer TypeScript enums.
 *
 * Each message is identified by a device-type-agnostic composite key:
 *   "<category>:<variant>:<enumValue>"
 *   e.g., "status:normal:7" for Status::Power (Normal variant)
 *
 * The device type (elevator, escalator, etc.) is NOT part of the key.
 * It is determined at assignment time when the adapter → channel → bank
 * relationship is established and the bank's device type is known.
 *
 * At that point, compositeKeyToValuedMessageT(key, active, deviceType)
 * produces the full FlatBuffer Object API ValuedMessageT.
 */
import { Status } from "../generated/status/status";
import { Traffic } from "../generated/traffic/traffic";
import { Usage } from "../generated/usage/usage";
import { Service } from "../generated/service/service";
import { Control } from "../generated/control/control";
import { Fault } from "../generated/fault/fault";
import { GroupSupervisoryStatus } from "../generated/group-supervisory-status/group-supervisory-status";
import { GroupControl } from "../generated/group-control/group-control";
import { ControllerInfoContent } from "../generated/controller-info/controller-info-content";
import { MessageContent } from "../generated/ln2/message-content";
import { EncodedMessageContent } from "../generated/ln2/encoded-message-content";
import { MessageT } from "../generated/ln2/message";
import { ValuedMessageT } from "../generated/ln2/valued-message";
import { compositeKeyToValuedMessageT, valuedMessageTToCompositeKey } from "./valuedMessageBuilder";

// =====================================================================
// Types
// =====================================================================

export type MessageVariant = "normal" | "valued";

/** Device types that determine the FlatBuffer wrapper at auto-expand time */
export type DeviceType = "elevator" | "escalator";

export interface Ln2MessageOption {
  /** Composite key: "<category>:<variant>:<enumValue>" */
  key: string;
  /** Human-readable label, e.g. "Power" */
  label: string;
  /** Numeric FlatBuffer enum value */
  enumValue: number;
  /** Normal or Valued */
  variant: MessageVariant;
}

export interface Ln2MessageCategory {
  /** Category key, e.g. "status", "traffic" */
  categoryKey: string;
  /** Generated FlatBuffer MessageContent discriminant for this category */
  messageContent: MessageContent;
  /** Human-readable category label */
  label: string;
  /** BitMask or OneHot encoding in the FlatBuffer wire format */
  encodedMessageContent: EncodedMessageContent;
  /** Whether position-based expansion applies (e.g., CarCall = per-floor) */
  positionBased: boolean;
  /** All enum variants in this category (both normal and valued) */
  options: Ln2MessageOption[];
}

// =====================================================================
// Helpers
// =====================================================================

/** Extracts [name, numericValue] pairs from a TypeScript numeric enum. */
function getEnumEntries<T extends Record<string, string | number>>(
  enm: T,
  skipNone = true,
): [string, number][] {
  return Object.entries(enm)
    .filter(([key, value]) => {
      if (typeof value !== "number") return false;
      if (skipNone && key === "NONE" && value === 0) return false;
      return true;
    })
    .map(([key, value]) => [key, value as number]);
}

/** Convert PascalCase/camelCase to spaced words: "CarCallLock" → "Car Call Lock" */
function humanize(name: string): string {
  return name.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/_/g, " ");
}

/** Traffic messages that are position-based (per-floor bitmask) */
const POSITION_BASED_TRAFFIC = new Set([
  "P",
  "CarCall",
  "RearCarCall",
  "HallCall",
  "UpHallCall",
  "UpRearHallCall",
  "DownHallCall",
  "DownRearHallCall",
  "CarCallAnswered",
  "RearCarCallAnswered",
  "HallCallAnswered",
  "UpHallCallAnswered",
  "UpRearHallCallAnswered",
  "DownHallCallAnswered",
  "DownRearHallCallAnswered",
]);

// =====================================================================
// Category builders
// =====================================================================

function buildCategoryOptions(
  enm: Record<string, string | number>,
  categoryKey: string,
): Ln2MessageOption[] {
  const entries = getEnumEntries(enm);
  const variants: MessageVariant[] = ["normal", "valued"];
  return variants.flatMap((variant) =>
    entries.map(([name, value]) => ({
      key: `${categoryKey}:${variant}:${value}`,
      label: humanize(name),
      enumValue: value,
      variant,
    })),
  );
}

// =====================================================================
// All categories (computed once — device-type-agnostic)
// =====================================================================

export const LN2_MESSAGE_CATEGORIES: Ln2MessageCategory[] = [
  {
    categoryKey: "status",
    messageContent: MessageContent.Status_StatusMessage,
    label: "Status",
    encodedMessageContent: EncodedMessageContent.BitMaskMessage,
    positionBased: false,
    options: buildCategoryOptions(Status, "status"),
  },
  {
    categoryKey: "traffic",
    messageContent: MessageContent.Traffic_TrafficMessage,
    label: "Traffic",
    encodedMessageContent: EncodedMessageContent.BitMaskMessage,
    positionBased: true,
    options: buildCategoryOptions(Traffic, "traffic"),
  },
  {
    categoryKey: "usage",
    messageContent: MessageContent.Usage_UsageMessage,
    label: "Usage",
    encodedMessageContent: EncodedMessageContent.OneHotMessage,
    positionBased: false,
    options: buildCategoryOptions(Usage, "usage"),
  },
  {
    categoryKey: "service",
    messageContent: MessageContent.Service_ServiceMessage,
    label: "Service",
    encodedMessageContent: EncodedMessageContent.OneHotMessage,
    positionBased: false,
    options: buildCategoryOptions(Service, "service"),
  },
  {
    categoryKey: "control",
    messageContent: MessageContent.Control_ControlMessage,
    label: "Control",
    encodedMessageContent: EncodedMessageContent.BitMaskMessage,
    positionBased: false,
    options: buildCategoryOptions(Control, "control"),
  },
  {
    categoryKey: "fault",
    messageContent: MessageContent.Fault_FaultMessage,
    label: "Fault",
    encodedMessageContent: EncodedMessageContent.BitMaskMessage,
    positionBased: false,
    options: buildCategoryOptions(Fault, "fault"),
  },
  {
    categoryKey: "group_supervisory",
    messageContent: MessageContent.GroupSupervisoryStatus_GroupSupervisoryStatusMessage,
    label: "Group Supervisory Status",
    encodedMessageContent: EncodedMessageContent.BitMaskMessage,
    positionBased: false,
    options: buildCategoryOptions(GroupSupervisoryStatus, "group_supervisory"),
  },
  {
    categoryKey: "group_control",
    messageContent: MessageContent.GroupControl_GroupControlMessage,
    label: "Group Control",
    encodedMessageContent: EncodedMessageContent.BitMaskMessage,
    positionBased: false,
    options: buildCategoryOptions(GroupControl, "group_control"),
  },
  {
    categoryKey: "controller_info",
    messageContent: MessageContent.ControllerInfo_ControllerInfoMessage,
    label: "Controller Info",
    encodedMessageContent: EncodedMessageContent.BitMaskMessage,
    positionBased: false,
    options: buildCategoryOptions(ControllerInfoContent, "controller_info"),
  },
];

/** Quick lookup: categoryKey → category */
const CATEGORY_MAP = new Map(LN2_MESSAGE_CATEGORIES.map((c) => [c.categoryKey, c]));

/** Bidirectional mapping between categoryKey strings and MessageContent enum. */
const CATEGORY_KEY_TO_MSG_CONTENT = new Map<string, MessageContent>(
  LN2_MESSAGE_CATEGORIES.map((c) => [c.categoryKey, c.messageContent]),
);
const MSG_CONTENT_TO_CATEGORY_KEY = new Map<MessageContent, string>(
  LN2_MESSAGE_CATEGORIES.map((c) => [c.messageContent, c.categoryKey]),
);

/**
 * Legacy / inconsistent category-key spellings that map to a canonical
 * `LN2_MESSAGE_CATEGORIES.categoryKey`. Parts of the bank-side ecosystem
 * (BankOffsetMatrix, StepBanks, StepReview, BanksCreateEditForm) use
 * `"group_supervisory_status"` whereas the canonical categoryKey here is
 * `"group_supervisory"`. This alias table lets both spellings round-trip
 * through `categoryKeyToMessageContent` and `messageContentToCategoryKey`
 * without a wider refactor.
 */
const CATEGORY_KEY_ALIASES: Record<string, string> = {
  group_supervisory_status: "group_supervisory",
};

/** Convert a category key string (e.g. "status") to its MessageContent enum value. */
export function categoryKeyToMessageContent(categoryKey: string): MessageContent {
  const canonical = CATEGORY_KEY_ALIASES[categoryKey] ?? categoryKey;
  return CATEGORY_KEY_TO_MSG_CONTENT.get(canonical) ?? MessageContent.NONE;
}

/** Convert a MessageContent enum value to its category key string. */
export function messageContentToCategoryKey(mc: MessageContent): string | undefined {
  return MSG_CONTENT_TO_CATEGORY_KEY.get(mc);
}

// =====================================================================
// Typed message filter (FlatBuffer-first alternative to Set<string>)
// =====================================================================

/**
 * Typed message filter keyed by FlatBuffer MessageContent discriminant.
 * Value is a Set of inner enum values (e.g., Status.Power = 7).
 *
 * Use this instead of Set<string> when filtering LN2 message dropdowns
 * to keep filtering fully typed against FlatBuffer generated enums.
 */
export type Ln2MessageFilter = Map<MessageContent, Set<number>>;

/**
 * Convert a typed Ln2MessageFilter into the internal categoryEnumKey set
 * used by SelectLn2Message for option matching.
 *
 * Each entry "category:enumValue" matches the option value format.
 */
export function ln2MessageFilterToKeySet(filter: Ln2MessageFilter): Set<string> {
  const keys = new Set<string>();
  filter.forEach((enumValues, mc) => {
    const catKey = MSG_CONTENT_TO_CATEGORY_KEY.get(mc);
    if (!catKey) return;
    enumValues.forEach((ev) => {
      keys.add(`${catKey}:${ev}`);
    });
  });
  return keys;
}

// =====================================================================
// Key parsing & display
// =====================================================================

export interface ParsedKey {
  categoryKey: string;
  variant: MessageVariant;
  enumValue: number;
}

/** Parse a composite key "category:variant:enumValue" into its parts. */
export function parseCompositeKey(key: string): ParsedKey | null {
  const parts = key.split(":");
  if (parts.length !== 3) return null;
  const [categoryKey, variantStr, enumStr] = parts;
  if (variantStr !== "normal" && variantStr !== "valued") return null;
  const enumValue = parseInt(enumStr, 10);
  if (Number.isNaN(enumValue)) return null;
  return { categoryKey, variant: variantStr, enumValue };
}

/** Get the human-readable display name for a composite key. */
export function getDisplayName(key: string): string {
  const parsed = parseCompositeKey(key);
  if (!parsed) return "--";
  const category = CATEGORY_MAP.get(parsed.categoryKey);
  if (!category) return "--";
  const option = category.options.find(
    (o) => o.enumValue === parsed.enumValue && o.variant === parsed.variant,
  );
  if (!option) return `${category.label}: ${parsed.enumValue}`;
  const variantLabel = parsed.variant === "valued" ? " (Valued)" : "";
  return `${category.label}: ${option.label}${variantLabel}`;
}

/**
 * "<Category>::<Option>" display name for a composite key — the same
 * double-colon format decodeFlattenedMessage's `fullName` produces from the
 * server's flattened (messageContent, messageValue) pair, and the same one
 * SelectLn2Message's own option list already uses.
 *
 * getDisplayName (above) uses a different, single-colon format that many
 * existing callers depend on, so it is left alone. This one exists for
 * callers that need what they show at selection time to read identically to
 * what a server round-trip (or a page reload) renders back — StepNotifications
 * is the first: its "Alerts when..." text parses "::" out of the stored
 * message name (LIFT-940), which getDisplayName's format doesn't contain.
 *
 * Returns "" (not "--") when the key doesn't resolve, matching what
 * decodeFlattenedMessage/messageKeyFromFlattened give an unresolvable pair.
 */
export function getFullDisplayName(key: string): string {
  const parsed = parseCompositeKey(key);
  if (!parsed) return "";
  const category = CATEGORY_MAP.get(parsed.categoryKey);
  if (!category) return "";
  const option = category.options.find(
    (o) => o.enumValue === parsed.enumValue && o.variant === parsed.variant,
  );
  if (!option) return "";
  return `${category.label}::${option.label}`;
}

/** A message decoded from the flattened (messageContent, messageValue) pair
 *  the backend persists on Keys and Notifications. */
export interface DecodedFlattenedMessage {
  /** Composite key "<category>:<variant>:<enumValue>" (normal variant). */
  key: string;
  /** Category label, e.g. "Status". */
  categoryLabel: string;
  /** Option label, e.g. "Power". */
  optionLabel: string;
  /** "<categoryLabel>::<optionLabel>". */
  fullName: string;
}

/** Reconstruct the composite key + labels from the flattened
 *  (messageContent, messageValue) pair. Inverse of the category ->
 *  messageContent and option -> enumValue mapping used when creating a
 *  notification. Returns null when the pair matches no known message. */
export function decodeFlattenedMessage(
  messageContent: number,
  messageValue: number,
): DecodedFlattenedMessage | null {
  const category = LN2_MESSAGE_CATEGORIES.find((c) => c.messageContent === messageContent);
  if (!category) return null;
  const option =
    category.options.find((o) => o.enumValue === messageValue && o.variant === "normal") ??
    category.options.find((o) => o.enumValue === messageValue);
  if (!option) return null;
  return {
    key: option.key,
    categoryLabel: category.label,
    optionLabel: option.label,
    fullName: `${category.label}::${option.label}`,
  };
}

/** Get the category for a composite key. */
export function getCategory(key: string): Ln2MessageCategory | null {
  const parsed = parseCompositeKey(key);
  if (!parsed) return null;
  return CATEGORY_MAP.get(parsed.categoryKey) ?? null;
}

/** Check if a message is position-based (e.g., Traffic::CarCall). */
export function isPositionBasedMessage(key: string): boolean {
  const parsed = parseCompositeKey(key);
  if (!parsed) return false;
  const category = CATEGORY_MAP.get(parsed.categoryKey);
  if (!category) return false;
  if (parsed.categoryKey === "traffic") {
    const name = getEnumEntries(Traffic).find(([, v]) => v === parsed.enumValue)?.[0];
    return name ? POSITION_BASED_TRAFFIC.has(name) : false;
  }
  return category.positionBased;
}

/**
 * The categories whose elevator arm has a `position` field: every
 * `ElevatorTrafficMessage` and every `ElevatorControlMessage`, whatever the
 * enum inside it. Keyed by the generated `MessageContent` discriminant so a
 * schema rename breaks the build here instead of silently matching nothing.
 */
const POSITION_CARRYING_CONTENT: ReadonlySet<MessageContent> = new Set([
  MessageContent.Traffic_TrafficMessage,
  MessageContent.Control_ControlMessage,
]);

/**
 * Whether the LN2 message behind a composite key has a `position` field a floor
 * can be put in: every elevator Traffic message and every elevator Control
 * message.
 *
 * `isPositionBasedMessage` answers a different question (which traffic messages
 * the adapter offset matrix expands per floor) and leaves out, among others,
 * the four `*HallCallAssigned` messages, although the monitoring decoder reads
 * their floor off `position` (`applyDeltaToState.ts`
 * `applyElevatorTrafficDelta`). It also reads Control's `positionBased` flag,
 * which is false even though `ElevatorControlMessage.position` exists.
 *
 * This is the question the OPC UA mapping editor asks: will the builder write a
 * floor for this row. It matches `attachLn2PositionFromRow` in
 * `opcUaCdoBuilder.ts` exactly, which writes `position` for any elevator
 * Traffic or Control message and for nothing else, so the field the editor
 * shows and the field the builder writes cannot disagree.
 */
export function messageCarriesPosition(key: string): boolean {
  const category = getCategory(key);
  return category !== null && POSITION_CARRYING_CONTENT.has(category.messageContent);
}

// =====================================================================
// Legacy JSON-format builders (deprecated — use compositeKeyToValuedMessageT)
// Kept for out-of-scope consumers (Numato, STM32, OPC adapters).
// =====================================================================

// Re-import the old JSON interface only for the legacy functions below.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type EncodedMsgContent = import("../../interfaces/AdapterCdoModbus.interface").EncodedMsgContent;

function buildBitmaskInner(categoryKey: string, enumValue: number): EncodedMsgContent | null {
  const messageKeyMap: Record<string, string> = {
    status: "status_message",
    traffic: "traffic_message",
    control: "control_message",
    fault: "fault_message",
    group_supervisory: "group_supervisory_status_message",
    group_control: "group_control_message",
    controller_info: "controller_info_message",
  };
  const msgKey = messageKeyMap[categoryKey];
  if (!msgKey) return null;
  return {
    bit_mask_message: { message: { [msgKey]: { message: enumValue } } },
  };
}

function buildOnehotInner(
  categoryKey: string,
  enumValue: number,
  deviceType: DeviceType,
): EncodedMsgContent | null {
  const devicePrefix = deviceType === "elevator" ? "elevator" : "escalator";
  if (categoryKey === "usage") {
    return {
      one_hot_message: {
        message: {
          usage_message: {
            message: {
              [`${devicePrefix}_usage_message`]: {
                message: { message: enumValue },
              },
            },
          },
        },
      },
    };
  }
  if (categoryKey === "service") {
    return {
      one_hot_message: {
        message: {
          service_message: {
            message: {
              [`${devicePrefix}_service_message`]: {
                message: { message: enumValue },
              },
            },
          },
        },
      },
    };
  }
  return null;
}

/** @deprecated Use compositeKeyToValuedMessageT() from valuedMessageBuilder.ts */
export function buildEncodedMsgContent(
  key: string,
  deviceType?: DeviceType,
): EncodedMsgContent | null {
  const parsed = parseCompositeKey(key);
  if (!parsed) return null;
  const category = CATEGORY_MAP.get(parsed.categoryKey);
  if (!category) return null;
  if (category.encodedMessageContent === EncodedMessageContent.BitMaskMessage) {
    return buildBitmaskInner(parsed.categoryKey, parsed.enumValue);
  }
  if (!deviceType) return null;
  return buildOnehotInner(parsed.categoryKey, parsed.enumValue, deviceType);
}

/**
 * Detect the LN2 category key ("status", "usage", …) from a nested-union
 * `EncodedMsgContent` JSON — the inverse of which arm `buildEncodedMsgContent`
 * populated. Returns null when no recognized message arm is present; callers
 * that need a non-null default (e.g. a Select fallback) coalesce with "status".
 * Single source of truth for `Ln2MessageTypeSelect.parseMessageType` and the
 * monitoring State-tab CDO walkers, so a new category only has to be added here.
 */
export function encodedMsgContentCategoryKey(content: EncodedMsgContent): string | null {
  if (content.one_hot_message?.message) {
    const m = content.one_hot_message.message;
    if (m.usage_message != null) return "usage";
    if (m.service_message != null) return "service";
  }
  if (content.bit_mask_message?.message) {
    const m = content.bit_mask_message.message as Record<string, unknown>;
    if (m.status_message != null) return "status";
    if (m.traffic_message != null) return "traffic";
    if (m.control_message != null) return "control";
    if (m.fault_message != null) return "fault";
    if (m.group_supervisory_status_message != null) return "group_supervisory";
    if (m.group_control_message != null) return "group_control";
    if (m.controller_info_message != null) return "controller_info";
  }
  return null;
}

// =====================================================================
// MessageT ↔ enum value conversion for exclusion config
// =====================================================================

/**
 * Build a MessageT from a category key and enum value.
 * Uses the existing valuedMessageBuilder infrastructure.
 */
export function buildMessageTForEnum(
  categoryKey: string,
  enumValue: number,
  variant: MessageVariant = "normal",
  deviceType: DeviceType = "elevator",
): MessageT | null {
  const key = `${categoryKey}:${variant}:${enumValue}`;
  const vm = compositeKeyToValuedMessageT(key, true, deviceType);
  return vm?.msg ?? null;
}

/**
 * Extract the numeric enum value from a MessageT.
 * Returns the enum value or -1 if not extractable.
 */
export function extractEnumValueFromMessageT(msg: MessageT): number {
  const vm = new ValuedMessageT(msg, true);
  const key = valuedMessageTToCompositeKey(vm);
  if (!key) return -1;
  const parts = key.split(":");
  if (parts.length < 3) return -1;
  const parsed = parseInt(parts[2], 10);
  return Number.isNaN(parsed) ? -1 : parsed;
}
